import { Fragment, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import L from 'leaflet';
import {
  AttributionControl,
  MapContainer,
  Polygon,
  TileLayer,
  Tooltip,
  ZoomControl,
  useMap,
} from 'react-leaflet';
import { toFootprintPayload, toPlaceForm } from '../../../../utils/placeForm';
import PolygonEditor, {
  defaultLocation,
  defaultMapZoom,
  footprintRings,
  maxMapZoom,
  maxTileZoom,
  minMapZoom,
} from './LocationTab';
import { Button, Typography, Alert, MenuItem, Paper, Stack, TextField } from '@mui/material';
const placeName = (place) =>
  place?.name?.he ||
  place?.name?.en ||
  place?.shortName?.he ||
  place?.shortName?.en ||
  place?.placeId ||
  '-';
const FloorUnitPlanner = ({ floor, units, location, onSave, onDelete }) => {
  const [selectedId, setSelectedId] = useState(units[0]?.unitId || 'new');
  const [name, setName] = useState(units[0]?.name || '');
  const [editorForm, setEditorForm] = useState(() =>
    toPlaceForm({
      location,
      footprint: units[0]?.footprint || null,
    }),
  );
  const [collisions, setCollisions] = useState([]);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [focusRequest, setFocusRequest] = useState(null);
  const selectedUnit = units.find((unit) => unit.unitId === selectedId) || null;
  const selectUnit = (unitId) => {
    const unit = units.find((item) => item.unitId === unitId) || null;
    setSelectedId(unitId);
    setName(unit?.name || '');
    setEditorForm(
      toPlaceForm({
        location,
        footprint: unit?.footprint || null,
      }),
    );
    setFocusRequest(
      unit
        ? {
          unitId,
          footprint: unit.footprint,
        }
        : null,
    );
    setCollisions([]);
    setMessage('');
  };
  const save = async () => {
    const payload = toFootprintPayload(editorForm);
    if (!name.trim() || !payload.footprint || collisions.length) return;
    setSaving(true);
    setMessage('');
    try {
      const saved = await onSave(selectedId, {
        name: name.trim(),
        floor,
        footprint: payload.footprint,
      });
      setSelectedId(saved.unitId);
      setMessage('היחידה נשמרה');
    } catch (error) {
      setMessage(error.message || 'שמירת היחידה נכשלה');
    } finally {
      setSaving(false);
    }
  };
  const duplicate = () => {
    if (!selectedUnit) return;
    setSelectedId('new');
    setName(`${selectedUnit.name} - עותק`);
    setEditorForm(
      toPlaceForm({
        location,
        footprint: selectedUnit.footprint,
      }),
    );
    setCollisions([]);
    setMessage('היחידה שוכפלה. הזיזו אותה למיקום החדש ושמרו.');
  };
  const remove = async () => {
    if (!selectedUnit || selectedUnit.assignedPlaceId) return;
    await onDelete(selectedUnit.unitId);
  };
  const nearby = units
    .filter((unit) => unit.unitId !== selectedId)
    .map((unit) => ({
      placeId: unit.unitId,
      name: {
        he: unit.name,
      },
      footprint: unit.footprint,
    }));
  return (
    <Stack
      sx={{
        display: 'grid',
        gap: '14px',
        paddingTop: '0',
        borderTop: '0',
        minHeight: '100%',
        height: '100%',
        gridTemplateRows: 'minmax(0, 1fr) auto',
      }}
    >
      <PolygonEditor
        placeForm={editorForm}
        setPlaceForm={setEditorForm}
        nearbyFootprints={nearby}
        nearbyFootprintsLoading={false}
        setFootprintCollisions={setCollisions}
        polygonOnly
        singlePart
        showHeightControl={false}
        focusRequest={focusRequest}
        polygonPanelContent={
          <Stack
            sx={{
              display: 'grid',
              gap: '12px',
              paddingBottom: '10px',
              borderBottom: '1px solid var(--line)',
              '& label': {
                display: 'grid',
                gap: '5px',
                color: 'var(--muted)',
                fontSize: '12px',
                fontWeight: '700',
              },
            }}
          >
            <Stack>
              <Typography>יחידה לעריכה</Typography>
              <TextField
                value={selectedId}
                onChange={(event) => selectUnit(event.target.value)}
                select
                fullWidth
              >
                <MenuItem value="new">+ יחידה חדשה</MenuItem>
                {units.map((unit) => (
                  <MenuItem key={unit.unitId} value={unit.unitId}>
                    {unit.name}
                    {unit.assignedPlaceId ? ' · מוקצית' : ' · פנויה'}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <Stack>
              <Typography>שם / מספר יחידה</Typography>
              <TextField
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="לדוגמה: חנות 12"
                fullWidth
              />
            </Stack>
          </Stack>
        }
      />
      <Stack
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          [theme.breakpoints.down(761)]: {
            alignItems: 'stretch',
            flexDirection: 'column',
          },
        })}
      >
        <Typography
          sx={String(message.includes('נכשלה') ? 'error' : 'success')
            .split(/\s+/)
            .map(
              (name) =>
                ({
                  success: {
                    borderRightColor: '#12b76a',
                    borderColor: '#b7ead4',
                    background: '#ecfdf3',
                    color: '#087443',
                    fontWeight: '700',
                  },
                  error: {
                    borderColor: '#fecdca',
                    background: '#fef3f2',
                    color: 'var(--danger)',
                    fontWeight: '700',
                  },
                })[name],
            )
            .filter(Boolean)}
        >
          {message}
        </Typography>
        {selectedUnit && (
          <Button
            type="button"
            onClick={duplicate}
            sx={{
              background: '#edf2f7',
              color: 'var(--text)',
              '&:hover': {
                background: '#ddebff',
                color: '#1269e8',
              },
            }}
          >
            שכפול יחידה
          </Button>
        )}
        {selectedUnit && (
          <Button
            type="button"
            disabled={Boolean(selectedUnit.assignedPlaceId)}
            title={selectedUnit.assignedPlaceId ? 'יש לפנות את המקום לפני מחיקת היחידה' : ''}
            onClick={remove}
            sx={{
              borderRightColor: '#d92d20',
              background: '#fee4e2',
              color: 'var(--danger)',
              '&:disabled': {
                opacity: '.45',
                cursor: 'not-allowed',
                transform: 'none',
              },
            }}
          >
            מחיקת יחידה
          </Button>
        )}
        <Button
          type="button"
          disabled={saving || !name.trim() || collisions.length > 0}
          onClick={save}
        >
          {saving ? 'שומר…' : 'שמירת יחידה'}
        </Button>
      </Stack>
    </Stack>
  );
};
const FloorUnitsFit = ({ units }) => {
  const map = useMap();
  useEffect(() => {
    const points = units.flatMap((unit) => footprintRings(unit.footprint).flat());
    if (!points.length) return;
    map.fitBounds(L.latLngBounds(points.map(([lng, lat]) => [lat, lng])), {
      padding: [90, 90],
      maxZoom: 21,
      animate: false,
    });
  }, [map, units]);
  return null;
};
const FloorUnitAssignmentMap = ({ floor, units, places, location, onSave }) => {
  const [draft, setDraft] = useState(() =>
    Object.fromEntries(units.map((unit) => [unit.unitId, unit.assignedPlaceId || ''])),
  );
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const center =
    location?.coordinates?.length === 2
      ? [location.coordinates[1], location.coordinates[0]]
      : [defaultLocation.lat, defaultLocation.lng];
  const selectedByOtherUnit = (unitId, placeId) =>
    Object.entries(draft).some(
      ([otherUnitId, selectedPlaceId]) => otherUnitId !== unitId && selectedPlaceId === placeId,
    );
  const save = async () => {
    setSaving(true);
    setMessage('');
    try {
      await onSave({
        floor,
        assignments: units.map((unit) => ({
          unitId: unit.unitId,
          placeId: draft[unit.unitId] || null,
        })),
      });
      setMessage('כל ההקצאות נשמרו');
    } catch (error) {
      setMessage(error.message || 'שמירת ההקצאות נכשלה');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Stack
      sx={{
        display: 'grid',
        gridTemplateRows: 'minmax(0, 1fr) auto',
        width: '100%',
        height: '100%',
        minHeight: '0',
      }}
    >
      <MapContainer
        className="floor-assignment-map"
        center={center}
        zoom={defaultMapZoom}
        minZoom={minMapZoom}
        maxZoom={maxMapZoom}
        zoomControl={false}
        attributionControl={false}
        scrollWheelZoom
      >
        <TileLayer
          attribution="&copy; OpenStreetMap contributors &copy; CARTO"
          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}.png"
          subdomains="abc"
          maxNativeZoom={maxTileZoom}
          maxZoom={maxMapZoom}
        />
        <FloorUnitsFit units={units} />
        <ZoomControl position="bottomright" />
        <AttributionControl position="bottomleft" prefix={false} />
        {units.map((unit) =>
          footprintRings(unit.footprint).map((ring, ringIndex) => (
            <Polygon
              key={`${unit.unitId}-${ringIndex}`}
              positions={ring.map(([lng, lat]) => [lat, lng])}
              pathOptions={{
                color: draft[unit.unitId] ? '#087443' : '#1677ff',
                weight: 3,
                fillColor: draft[unit.unitId] ? '#12b76a' : '#53a2ff',
                fillOpacity: 0.28,
              }}
            >
              {ringIndex === 0 && (
                <Tooltip
                  permanent
                  interactive
                  direction="center"
                  className="floor-unit-assignment-label"
                >
                  <Stack
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Typography>{unit.name}</Typography>
                    <TextField
                      value={draft[unit.unitId] || ''}
                      onChange={(event) =>
                        setDraft((current) => ({
                          ...current,
                          [unit.unitId]: event.target.value,
                        }))
                      }
                      select
                      fullWidth
                    >
                      <MenuItem value="">ללא מקום</MenuItem>
                      {places.map((place) => (
                        <MenuItem
                          key={place.placeId}
                          value={place.placeId}
                          disabled={selectedByOtherUnit(unit.unitId, place.placeId)}
                        >
                          {placeName(place)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                </Tooltip>
              )}
            </Polygon>
          )),
        )}
      </MapContainer>
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          justifyContent: 'end',
          gap: '16px',
          padding: '12px 18px',
          borderTop: '1px solid var(--line)',
          background: 'var(--panel)',
        }}
      >
        <Typography>{message}</Typography>

        <Typography>
          {units.length} יחידות · {Object.values(draft).filter(Boolean).length} מוקצות
        </Typography>
        <Button type="button" onClick={save} disabled={saving}>
          {saving ? 'שומר…' : 'שמירת כל ההקצאות'}
        </Button>
      </Stack>
    </Stack>
  );
};
const ContainerManager = ({
  placeId,
  setPlaceForm,
  onLoad,
  onUpdateFloors,
  onManageFloorUnit,
  onAssignPlace,
  onRemovePlace,
  onSavePlaceFootprint,
}) => {
  const [data, setData] = useState({
    floors: [],
    children: [],
    candidates: [],
  });
  const [activeFloor, setActiveFloor] = useState('');
  const [newFloor, setNewFloor] = useState('');
  const [editingFloor, setEditingFloor] = useState('');
  const [editingValue, setEditingValue] = useState('');
  const [search, setSearch] = useState('');
  const [loadingManager, setLoadingManager] = useState(true);
  const [managerError, setManagerError] = useState('');
  const [polygonFloor, setPolygonFloor] = useState('');
  const [assignmentFloor, setAssignmentFloor] = useState('');
  const load = async (searchValue = '', silent = false) => {
    if (!silent) setLoadingManager(true);
    setManagerError('');
    try {
      const next = await onLoad(placeId, searchValue);
      setData(next);
      setActiveFloor((current) => (next.floors.includes(current) ? current : next.floors[0] || ''));
      setPlaceForm((current) => ({
        ...current,
        floorsText: next.floors.join('\n'),
      }));
    } catch (error) {
      setManagerError(error.message || 'טעינת נתוני המתחם נכשלה');
    } finally {
      if (!silent) setLoadingManager(false);
    }
  };
  useEffect(() => {
    load();
    // The manager is remounted for each selected place.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeId]);
  const saveFloors = async (floors, rename) => {
    setManagerError('');
    try {
      const result = await onUpdateFloors(placeId, {
        floors,
        ...(rename
          ? {
            rename,
          }
          : {}),
      });
      const nextFloors = result.floors || floors;
      setData((current) => ({
        ...current,
        floors: nextFloors,
      }));
      setPlaceForm((current) => ({
        ...current,
        floorsText: nextFloors.join('\n'),
      }));
      setActiveFloor((current) =>
        rename?.from === current
          ? rename.to
          : nextFloors.includes(current)
            ? current
            : nextFloors[0] || '',
      );
      if (rename) {
        setData((current) => ({
          ...current,
          children: current.children.map((child) =>
            child.containment?.floor === rename.from
              ? {
                ...child,
                containment: {
                  ...child.containment,
                  floor: rename.to,
                },
              }
              : child,
          ),
        }));
      }
      return true;
    } catch (error) {
      setManagerError(error.message || 'שמירת הקומות נכשלה');
      return false;
    }
  };
  const addFloor = async () => {
    const floor = newFloor.trim();
    if (!floor || data.floors.includes(floor)) return;
    if (await saveFloors([...data.floors, floor])) {
      setNewFloor('');
      setActiveFloor(floor);
    }
  };
  const renameFloor = async (floor) => {
    const nextName = editingValue.trim();
    if (!nextName || (nextName !== floor && data.floors.includes(nextName))) return;
    const floors = data.floors.map((item) => (item === floor ? nextName : item));
    if (
      await saveFloors(floors, {
        from: floor,
        to: nextName,
      })
    ) {
      setEditingFloor('');
      setEditingValue('');
    }
  };
  const removeFloor = async (floor) => {
    const count = data.children.filter((child) => child.containment?.floor === floor).length;
    if (count > 0) return;
    await saveFloors(data.floors.filter((item) => item !== floor));
  };
  const assignPlace = async (candidate, targetFloor = activeFloor) => {
    if (!targetFloor) return;
    setManagerError('');
    try {
      await onAssignPlace(placeId, {
        placeId: candidate.placeId,
        floor: targetFloor,
      });
      await load(search, true);
    } catch (error) {
      setManagerError(error.message || 'שיוך המקום נכשל');
    }
  };
  const manageFloorUnit = async (unitId, action, payload) => {
    setManagerError('');
    try {
      const result = await onManageFloorUnit(placeId, unitId, action, payload);
      await load(search, true);
      return result;
    } catch (error) {
      setManagerError(error.message || 'פעולת היחידה נכשלה');
      throw error;
    }
  };
  const removePlace = async (child) => {
    setManagerError('');
    try {
      await onRemovePlace(placeId, child.placeId);
      await load(search, true);
    } catch (error) {
      setManagerError(error.message || 'הסרת המקום מהמתחם נכשלה');
    }
  };
  const floorPlaces = data.children.filter((child) => child.containment?.floor === activeFloor);
  const floorUnits = (data.floorUnits || []).filter((unit) => unit.floor === activeFloor);
  const assignablePlaces = [...data.children, ...data.candidates].filter(
    (place, index, all) =>
      !floorUnits.some((unit) => unit.assignedPlaceId === place.placeId) &&
      all.findIndex((item) => item.placeId === place.placeId) === index,
  );
  const unassignedPlaces = data.children.filter(
    (child) => !child.containment?.floor && child.status === 'active',
  );
  return (
    <Stack
      sx={{
        width: "100%",
        display: 'grid',
        gap: '18px',
      }}
    >
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          gap: '20px',
        }}
      >
        <Stack>
          <Typography>ניהול מתחם</Typography>
          <Typography
            sx={{
              fontSize: 12,
            }}
          >
            הגדירו קומות ושייכו אליהן מקומות קיימים מהמאגר.
          </Typography>
        </Stack>

        <Stack
          direction="row"
          sx={{
            gap: '8px',
            alignItems: 'center',
          }}
        >
          <TextField
            value={newFloor}
            placeholder="שם קומה חדשה"
            onChange={(event) => setNewFloor(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                addFloor();
              }
            }}
          />

          <Button onClick={addFloor} disabled={!newFloor.trim()}>
            הוספת קומה
          </Button>
        </Stack>
      </Stack>

      {managerError && (
        <Alert
          sx={{
            padding: '11px 14px',
            borderRadius: '10px',
            fontWeight: '700',
            background: '#fef3f2',
            color: 'var(--danger)',
          }}
        >
          {managerError}
        </Alert>
      )}

      <Stack
        direction="row"
        sx={{
          gap: '8px',
          paddingBottom: '3px',
        }}
      >
        {data.floors.map((floor) => {
          const count = data.children.filter((child) => child.containment?.floor === floor).length;
          return (
            <Button
              type="button"
              role="tab"
              aria-selected={activeFloor === floor}
              key={floor}
              onClick={() => setActiveFloor(floor)}
            >
              <Typography
                sx={{
                  fontSize: 12,
                }}
              >
                {`${floor} (${count})`}
              </Typography>
            </Button>
          );
        })}
      </Stack>

      {!data.floors.length ? (
        <Typography
          sx={{
            padding: '38px 18px',
            border: '1px dashed var(--line)',
            borderRadius: '14px',
            color: 'var(--muted)',
            textAlign: 'center',
            background: '#f8fafc',
          }}
        >
          עדיין לא הוגדרו קומות במתחם.
        </Typography>
      ) : (
        <Stack
          sx={{
            display: 'grid',
            gap: '16px',
            padding: '18px',
            border: '1px solid var(--line)',
            borderRadius: '16px',
            background: '#fff',
          }}
        >
          <Stack
            sx={{
              justifyContent: 'space-between',
              gap: '16px',
            }}
          >
            {editingFloor === activeFloor ? (
              <Stack
                direction="row"
                sx={{
                  gap: '8px',
                  alignItems: 'center',
                  flex: '1',
                }}
              >
                <TextField
                  value={editingValue}
                  onChange={(event) => setEditingValue(event.target.value)}
                  autoFocus
                />
                <Button type="button" onClick={() => renameFloor(activeFloor)}>
                  שמירה
                </Button>
                <Button
                  type="button"
                  onClick={() => setEditingFloor('')}
                  sx={{
                    background: '#edf2f7',
                    color: 'var(--text)',
                    '&:hover': {
                      background: '#ddebff',
                      color: '#1269e8',
                    },
                  }}
                >
                  ביטול
                </Button>
              </Stack>
            ) : (
              <Fragment>
                <Stack>
                  <Typography>{activeFloor}</Typography>
                  <Typography>
                    {floorUnits.length} יחידות ·{' '}
                    {floorUnits.filter((unit) => unit.assignedPlaceId).length} מוקצות
                  </Typography>
                </Stack>

                <Stack
                  direction="row"
                  sx={{
                    gap: 2,
                  }}
                >
                  <Button
                    onClick={() => setPolygonFloor(activeFloor)}
                    title="תכנון ועריכת יחידות בקומה"
                  >
                    תכנון יחידות
                  </Button>

                  <Button
                    disabled={!floorUnits.length}
                    title={
                      !floorUnits.length ? 'אין יחידות בקומה להקצאה' : 'הקצאת מקומות על מפת היחידות'
                    }
                    onClick={() => setAssignmentFloor(activeFloor)}
                    sx={{
                      background: '#edf2f7',
                      color: 'var(--text)',
                      '&:hover': {
                        background: '#ddebff',
                        color: '#1269e8',
                      },
                    }}
                  >
                    יחידות בקומה
                  </Button>

                  <Button
                    onClick={() => {
                      setEditingFloor(activeFloor);
                      setEditingValue(activeFloor);
                    }}
                    sx={{
                      background: '#edf2f7',
                      color: 'var(--text)',
                      '&:hover': {
                        background: '#ddebff',
                        color: '#1269e8',
                      },
                    }}
                  >
                    עריכת שם
                  </Button>

                  <Button
                    disabled={floorPlaces.length > 0 || floorUnits.length > 0}
                    title={
                      floorPlaces.length || floorUnits.length
                        ? 'אי אפשר למחוק קומה שיש בה מקומות או יחידות'
                        : ''
                    }
                    onClick={() => removeFloor(activeFloor)}
                    sx={{
                      borderRightColor: '#d92d20',
                      background: '#fee4e2',
                      color: 'var(--danger)',
                      '&:disabled': {
                        opacity: '.45',
                        cursor: 'not-allowed',
                        transform: 'none',
                      },
                    }}
                  >
                    מחיקת קומה
                  </Button>
                </Stack>
              </Fragment>
            )}
          </Stack>

          <Stack
            sx={{
              display: 'grid',
              gap: '8px',
            }}
          >
            {floorPlaces.map((child) => (
              <Stack
                key={child.placeId}
                direction="row"
                sx={{
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '14px',
                  padding: '12px 14px',
                  border: '1px solid var(--line)',
                  borderRadius: '12px',
                  background: '#fbfcfd',
                }}
              >
                <Stack>
                  <Typography
                    sx={String(
                      child.footprint ? 'place-footprint-present' : 'place-footprint-missing',
                    )
                      .split(/\s+/)
                      .map(
                        (name) =>
                          ({
                            'place-footprint-present': {
                              color: '#087443',
                            },
                            'place-footprint-missing': {
                              color: '#d92d20',
                            },
                          })[name],
                      )
                      .filter(Boolean)}
                  >
                    {placeName(child)}
                  </Typography>
                  <Typography>{child.placeId}</Typography>
                </Stack>
                <Button
                  type="button"
                  onClick={() => {
                    const assignedUnit = floorUnits.find(
                      (unit) => unit.assignedPlaceId === child.placeId,
                    );
                    return assignedUnit
                      ? manageFloorUnit(assignedUnit.unitId, 'unassign')
                      : removePlace(child);
                  }}
                  sx={{
                    background: '#edf2f7',
                    color: 'var(--text)',
                    '&:hover': {
                      background: '#ddebff',
                      color: '#1269e8',
                    },
                  }}
                >
                  הסרה מהמתחם
                </Button>
              </Stack>
            ))}
            {!floorPlaces.length && (
              <Typography
                sx={(theme) => ({
                  padding: '8px',
                  border: '1px dashed var(--line)',
                  borderRadius: '12px',
                  color: 'var(--muted)',
                  textAlign: 'center',
                  background: '#f8fafc',
                  gridTemplateColumns: 'minmax(180px, 1fr) 125px 145px 72px',
                  [theme.breakpoints.down('lg')]: {
                    gridTemplateColumns: '1fr 1fr',
                    minWidth: '0',
                  },
                  [theme.breakpoints.down('sm')]: {
                    gridTemplateColumns: 'minmax(0, 1fr)',
                    display: 'grid',
                    width: '100%',
                    minWidth: '0',
                    gap: '8px',
                    margin: '0',
                    padding: '12px',
                    overflowX: 'hidden',
                    overscrollBehaviorInline: 'contain',
                    scrollSnapType: 'none',
                    scrollbarWidth: 'thin',
                    WebkitOverflowScrolling: 'touch',
                    maxHeight: 'min(80dvh, 640px)',
                    overflowY: 'auto',
                    borderRadius: '16px',
                    background: '#fff',
                    boxShadow: '0 -18px 50px rgba(15,23,42,.2)',
                  },
                  gap: '8px',
                })}
              >
                אין מקומות בקומה הזו.
              </Typography>
            )}
          </Stack>

          <Stack
            sx={{
              display: 'grid',
              gap: '12px',
              paddingTop: '16px',
              borderTop: '1px solid var(--line)',
            }}
          >
            <Stack>
              <Typography>חיפוש מקומות להקצאה</Typography>
              <Typography>חפשו מקום, ואז בחרו אותו מתוך יחידה פנויה למעלה.</Typography>
            </Stack>

            <Stack
              direction="row"
              sx={{
                gap: '8px',
                alignItems: 'center',
              }}
            >
              <TextField
                value={search}
                placeholder="חיפוש לפי שם או מזהה מקום"
                onChange={(event) => setSearch(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    load(search);
                  }
                }}
              />

              <Button
                type="button"
                onClick={() => load(search)}
                disabled={search.trim().length < 2}
                sx={{
                  background: '#edf2f7',
                  color: 'var(--text)',
                  '&:hover': {
                    background: '#ddebff',
                    color: '#1269e8',
                  },
                }}
              >
                חיפוש
              </Button>
            </Stack>

            <Stack
              sx={{
                display: 'grid',
                gap: '8px',
              }}
            >
              {data.candidates.map((candidate) => (
                <Stack
                  key={candidate.placeId}
                  direction="row"
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '14px',
                    padding: '12px 14px',
                    border: '1px solid var(--line)',
                    borderRadius: '12px',
                    background: '#fbfcfd',
                  }}
                >
                  <Stack>
                    <Typography
                      sx={{
                        color: candidate.placeKind === 'unit' ? 'error.main' : 'success.main',
                      }}
                    >
                      {placeName(candidate)}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: 11,
                      }}
                    >
                      {candidate.placeId}
                    </Typography>
                  </Stack>

                  <Button
                    type="button"
                    onClick={() => assignPlace(candidate)}
                    disabled={candidate.placeKind === 'unit'}
                    sx={{
                      background: '#edf2f7',
                      color: 'var(--text)',
                      '&:hover': {
                        background: '#ddebff',
                        color: '#1269e8',
                      },
                      '&:disabled': {
                        opacity: '.45',
                        cursor: 'not-allowed',
                        transform: 'none',
                      },
                    }}
                  >
                    הקצאה לקומה זו
                  </Button>
                </Stack>
              ))}
              {search.trim().length >= 2 && !data.candidates.length && (
                <Typography
                  sx={(theme) => ({
                    padding: '8px',
                    border: '1px dashed var(--line)',
                    borderRadius: '12px',
                    color: 'var(--muted)',
                    textAlign: 'center',
                    background: '#f8fafc',
                    gridTemplateColumns: 'minmax(180px, 1fr) 125px 145px 72px',
                    [theme.breakpoints.down('lg')]: {
                      gridTemplateColumns: '1fr 1fr',
                      minWidth: '0',
                    },
                    [theme.breakpoints.down('sm')]: {
                      gridTemplateColumns: 'minmax(0, 1fr)',
                      display: 'grid',
                      width: '100%',
                      minWidth: '0',
                      gap: '8px',
                      margin: '0',
                      padding: '12px',
                      overflowX: 'hidden',
                      overscrollBehaviorInline: 'contain',
                      scrollSnapType: 'none',
                      scrollbarWidth: 'thin',
                      WebkitOverflowScrolling: 'touch',
                      maxHeight: 'min(80dvh, 640px)',
                      overflowY: 'auto',
                      borderRadius: '16px',
                      background: '#fff',
                      boxShadow: '0 -18px 50px rgba(15,23,42,.2)',
                    },
                    gap: '8px',
                  })}
                >
                  לא נמצאו מקומות מתאימים.
                </Typography>
              )}
            </Stack>
          </Stack>
        </Stack>
      )}

      {!!unassignedPlaces.length && (
        <Stack
          sx={(theme) => ({
            display: 'grid',
            gap: '12px',
            padding: '18px',
            border: '1px solid #f1cf86',
            borderRadius: '16px',
            background: '#fffbf2',
            '& > header': {
              display: 'flex',
              alignItems: 'start',
              justifyContent: 'space-between',
              gap: '18px',
            },
            '& header p': {
              margin: '0',
              color: 'var(--muted)',
              fontSize: '13px',
            },
            [theme.breakpoints.down('sm')]: {
              '& > header': {
                alignItems: 'stretch',
                flexDirection: 'column',
              },
            },
          })}
        >
          <Stack>
            <Stack>
              <Typography>מקומות ללא קומה</Typography>
              <Typography>{unassignedPlaces.length} מקומות ממתינים לשיוך</Typography>
            </Stack>
            <Typography>בחרו קומה והמקום ישויך אליה מיד.</Typography>
          </Stack>
          <Stack
            sx={{
              display: 'grid',
              gap: '8px',
            }}
          >
            {unassignedPlaces.map((child) => (
              <Stack
                key={child.placeId}
                sx={(theme) => ({
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '14px',
                  padding: '12px 14px',
                  border: '1px solid var(--line)',
                  borderRadius: '12px',
                  background: '#fbfcfd',
                  '& > div': {
                    display: 'grid',
                    gap: '3px',
                    minWidth: '0',
                  },
                  '& strong': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                  '& small': {
                    color: 'var(--muted)',
                  },
                  [theme.breakpoints.down('sm')]: {
                    alignItems: 'stretch',
                    flexDirection: 'column',
                    '& button': {
                      width: '100%',
                    },
                  },
                })}
              >
                <Stack>
                  <Typography>{placeName(child)}</Typography>
                  <Typography>{child.placeId}</Typography>
                </Stack>
                <Stack
                  sx={(theme) => ({
                    minWidth: '220px',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '9px',
                    '& select': {
                      minWidth: '130px',
                    },
                    [theme.breakpoints.down('sm')]: {
                      alignItems: 'stretch',
                      flexDirection: 'column',
                      minWidth: '0',
                    },
                  })}
                >
                  <Typography>בחירת קומה</Typography>
                  <TextField
                    value=""
                    disabled={!data.floors.length}
                    onChange={(event) => {
                      const floor = event.target.value;
                      if (floor) assignPlace(child, floor);
                    }}
                    select
                    fullWidth
                  >
                    <MenuItem value="">בחרו קומה…</MenuItem>
                    {data.floors.map((floor) => (
                      <MenuItem key={floor} value={floor}>
                        {floor}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </Stack>
            ))}
          </Stack>
          {!data.floors.length && (
            <Alert
              sx={{
                padding: '11px 14px',
                borderRadius: '10px',
                fontWeight: '700',
                background: '#fff7e6',
                color: '#935f00',
              }}
            >
              יש להוסיף לפחות קומה אחת לפני שיוך המקומות.
            </Alert>
          )}
        </Stack>
      )}

      {polygonFloor &&
        createPortal(
          <Stack
            onMouseDown={() => setPolygonFloor('')}
            sx={(theme) => ({
              position: 'fixed',
              inset: '0',
              zIndex: '45',
              display: 'grid',
              placeItems: 'stretch',
              padding: '0',
              background: 'rgba(21, 33, 31, .62)',
              backdropFilter: 'blur(6px)',
              [theme.breakpoints.down(761)]: {
                padding: '0',
              },
            })}
          >
            <Stack
              role="dialog"
              aria-modal="true"
              aria-label={`תכנון יחידות בקומה ${polygonFloor}`}
              onMouseDown={(event) => event.stopPropagation()}
              sx={(theme) => ({
                width: '100vw',
                height: '100dvh',
                maxHeight: 'none',
                minHeight: '0',
                display: 'grid',
                contain: 'layout',
                gridTemplateRows: 'auto minmax(0, 1fr)',
                overflow: 'hidden',
                borderRadius: '0',
                background: 'var(--panel)',
                boxShadow: '0 28px 90px rgba(15, 23, 42, .32)',
                [theme.breakpoints.down(761)]: {
                  width: '100%',
                  height: '100vh',
                  maxHeight: '100vh',
                  minHeight: '100vh',
                  borderRadius: '0',
                },
              })}
            >
              <Stack
                sx={(theme) => ({
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '16px',
                  padding: '18px 22px',
                  borderBottom: '1px solid var(--line)',
                  [theme.breakpoints.down(761)]: {
                    alignItems: 'stretch',
                    flexDirection: 'column',
                  },
                })}
              >
                <Stack>
                  <Typography>תכנון יחידות · {polygonFloor}</Typography>
                  <Typography>צרו וערכו יחידות קבועות, ולאחר מכן הקצו אליהן מקומות.</Typography>
                </Stack>
                <Button
                  type="button"
                  onClick={() => setPolygonFloor('')}
                  sx={{
                    background: '#edf2f7',
                    color: 'var(--text)',
                    '&:hover': {
                      background: '#ddebff',
                      color: '#1269e8',
                    },
                  }}
                >
                  סגירה
                </Button>
              </Stack>
              <Paper
                sx={{
                  minHeight: '0',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  scrollbarGutter: 'stable',
                  padding: '0',
                  overflow: 'hidden',
                }}
              >
                <FloorUnitPlanner
                  key={polygonFloor}
                  floor={polygonFloor}
                  units={(data.floorUnits || []).filter((unit) => unit.floor === polygonFloor)}
                  location={data.location}
                  onSave={(unitId, payload) => manageFloorUnit(unitId, 'save', payload)}
                  onDelete={(unitId) => manageFloorUnit(unitId, 'delete')}
                />
              </Paper>
            </Stack>
          </Stack>,
          document.body,
        )}

      {assignmentFloor &&
        createPortal(
          <Stack
            onMouseDown={() => setAssignmentFloor('')}
            sx={(theme) => ({
              position: 'fixed',
              inset: '0',
              zIndex: '45',
              display: 'grid',
              placeItems: 'stretch',
              padding: '0',
              background: 'rgba(21, 33, 31, .62)',
              backdropFilter: 'blur(6px)',
              [theme.breakpoints.down(761)]: {
                padding: '0',
              },
            })}
          >
            <Stack
              role="dialog"
              aria-modal="true"
              aria-label={`הקצאת יחידות בקומה ${assignmentFloor}`}
              onMouseDown={(event) => event.stopPropagation()}
              sx={(theme) => ({
                width: '100vw',
                height: '100dvh',
                maxHeight: 'none',
                minHeight: '0',
                display: 'grid',
                contain: 'layout',
                gridTemplateRows: 'auto minmax(0, 1fr)',
                overflow: 'hidden',
                borderRadius: '0',
                background: 'var(--panel)',
                boxShadow: '0 28px 90px rgba(15, 23, 42, .32)',
                [theme.breakpoints.down(761)]: {
                  width: '100%',
                  height: '100vh',
                  maxHeight: '100vh',
                  minHeight: '100vh',
                  borderRadius: '0',
                },
              })}
            >
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '16px',
                  padding: '18px 22px',
                  borderBottom: '1px solid var(--line)',
                }}
              >
                <Stack>
                  <Typography>יחידות בקומה · {assignmentFloor}</Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                    }}
                  >
                    בחרו מקום משויך לקומה בתוך כל יחידה ושמרו את כל ההקצאות יחד.
                  </Typography>
                </Stack>
                <Button
                  type="button"
                  onClick={() => setAssignmentFloor('')}
                  sx={{
                    background: '#edf2f7',
                    color: 'var(--text)',
                    '&:hover': {
                      background: '#ddebff',
                      color: '#1269e8',
                    },
                  }}
                >
                  סגירה
                </Button>
              </Stack>
              <Paper
                sx={{
                  minHeight: '0',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  scrollbarGutter: 'stable',
                  padding: '0',
                  overflow: 'hidden',
                }}
              >
                <FloorUnitAssignmentMap
                  key={assignmentFloor}
                  floor={assignmentFloor}
                  units={(data.floorUnits || []).filter((unit) => unit.floor === assignmentFloor)}
                  places={data.children.filter(
                    (place) => place.containment?.floor === assignmentFloor,
                  )}
                  location={data.location}
                  onSave={(payload) => manageFloorUnit('assignments', 'bulk', payload)}
                />
              </Paper>
            </Stack>
          </Stack>,
          document.body,
        )}
    </Stack>
  );
};
export default ContainerManager;
